import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { IFLogo } from "@/components/Layout";
import { useUser } from "@/context/UserContext";
import api from "@/utils/api";

export default function Auth({ mode: initialMode }) {
  const [searchParams] = useSearchParams();
  const requestedRole = searchParams.get("role");
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({
    email: "", password: "", name: "",
    user_type: requestedRole === "interpreter" ? "interpreter" : "client",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useUser();
  const navigate = useNavigate();

  // Only preselect supported registration roles. The server still determines
  // the authenticated role; a query string never changes an existing account.
  useEffect(() => {
    if (requestedRole === "client" || requestedRole === "interpreter") {
      setForm((previous) => ({ ...previous, user_type: requestedRole }));
    }
  }, [requestedRole]);

  useEffect(() => {
    setMode(initialMode);
    setError("");
  }, [initialMode]);

  const getSubmitLabel = () => {
    if (loading) return "Bitte warten...";
    return mode === "login" ? "Anmelden" : "Konto erstellen";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && form.password.length < 8) {
      setError("Bitte wähle ein Passwort mit mindestens 8 Zeichen.");
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/users/register" : "/users/login";
      const payload = mode === "register"
        ? form
        : { email: form.email, password: form.password };
      const res = await api.post(endpoint, payload);
      login(res.data);
      const userType = res.data.user?.user_type || res.data.user_type;
      navigate(userType === "client" ? "/kunde/suche" : "/dolmetscher/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Ein Fehler ist aufgetreten.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-teal-50 to-[#14B8A6]/10 flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><IFLogo size="lg" /></div>
          <h1 className="text-2xl font-bold text-gray-900">Interpreter Finder</h1>
          <p className="text-gray-500 text-sm mt-1">Eine App für Kund:innen und Dolmetscher:innen</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-5 sm:p-8">
          <div className="flex rounded-2xl bg-gray-100 p-1 mb-6">
            <button
              onClick={() => { setMode("login"); setError(""); }}
              data-testid="tab-login"
              aria-pressed={mode === "login"}
              className={`flex-1 py-2.5 text-sm font-medium rounded-xl transition-all ${mode === "login" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
            >Anmelden</button>
            <button
              onClick={() => { setMode("register"); setError(""); }}
              data-testid="tab-register"
              aria-pressed={mode === "register"}
              className={`flex-1 py-2.5 text-sm font-medium rounded-xl transition-all ${mode === "register" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
            >Registrieren</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-2">Ich bin...</legend>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, user_type: "client" })}
                    data-testid="user-type-deaf"
                    aria-pressed={form.user_type === "client"}
                    className={`p-3 min-w-0 rounded-2xl border-2 text-center transition-all ${form.user_type === "client" ? "border-[#14B8A6] bg-[#FEF3CE]" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <div className="text-2xl mb-1" aria-hidden="true">🧏</div>
                    <p className="text-sm font-medium text-gray-900">Kund:in</p>
                    <p className="text-xs text-gray-500 mt-0.5">Gehörlos oder hörend</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, user_type: "interpreter" })}
                    data-testid="user-type-interpreter"
                    aria-pressed={form.user_type === "interpreter"}
                    className={`p-3 min-w-0 rounded-2xl border-2 text-center transition-all ${form.user_type === "interpreter" ? "border-[#14B8A6] bg-[#FEF3CE]" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <div className="text-2xl mb-1" aria-hidden="true">🤟</div>
                    <p className="text-sm font-medium text-gray-900 break-words">Dolmetscher:in</p>
                  </button>
                </div>
              </fieldset>
            )}

            {mode === "register" && (
              <div>
                <label htmlFor="auth-name" className="block text-sm font-medium text-gray-700 mb-1">Vollständiger Name</label>
                <input id="auth-name" type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Max Mustermann" required autoComplete="name" data-testid="input-name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent" />
              </div>
            )}

            <div>
              <label htmlFor="auth-email" className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Adresse</label>
              <input id="auth-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@beispiel.de" required autoComplete="email" data-testid="input-email"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent" />
            </div>

            <div>
              <label htmlFor="auth-password" className="block text-sm font-medium text-gray-700 mb-1">
                Passwort {mode === "register" && <span className="text-gray-400 text-xs font-normal">(mind. 8 Zeichen)</span>}
              </label>
              <input id="auth-password" type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••" required minLength={mode === "register" ? 8 : undefined}
                autoComplete={mode === "register" ? "new-password" : "current-password"} data-testid="input-password"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent" />
            </div>

            {error && <div role="alert" data-testid="auth-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full py-4 bg-[#14B8A6] text-gray-900 font-semibold rounded-xl hover:bg-[#0D9488] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {getSubmitLabel()}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            {mode === "login" ? (
              <>Noch kein Konto? <button onClick={() => { setMode("register"); setError(""); }} className="text-[#0D9488] font-medium">Registrieren</button></>
            ) : (
              <>Bereits registriert? <button onClick={() => { setMode("login"); setError(""); }} className="text-[#0D9488] font-medium">Anmelden</button></>
            )}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
